import { useEffect, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import AddButton from "../../../../components/AddButton";
import CancelButton from "../../../../components/CancelButton";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import CloseIconButton from "../../../../components/CloseIconButton";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import MinusButton from "../../../../components/MinusButton";
import Modal from "../../../../components/Modal";
import OfficeKeyCard from "../../../../components/OfficeKeyCard";
import PlusButton from "../../../../components/PlusButton";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import SelectHookForm_3 from "../../../../components/SelectHookForm_3";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { useEmployees } from "../../Employees/Confie/hooks/useEmployees";
import {
  officeKeyModalState,
  officeKeysAddState,
  officeKeysSaving,
  SecurityAddState,
  SecurityEditState,
  OfficeKeysForEdit,
  okDeactivateModal,
} from "../atoms/atoms";
import useAddSingleOfficeKeys from "../hooks/useAddSingleOfficeKeys";
import useDeleteOfficeKey from "../hooks/useDeleteOfficeKey";
import useEditSingleOfficeKey from "../hooks/useEditSingleOfficeKey";
import { useGetOfficeKeysByRecordNumber } from "../hooks/useGetOfficeKeysByRecordNumber";
import { useVendors } from "../hooks/useVendors";
import { IOfficeKeys } from "../types";
import { useNumberOfKeys } from "../hooks/useNumberOfKeys";
import { useGetKeyType } from "../hooks/useGetKeyType";
import SecurityModal from "../../../../components/SecurityModal";

const OfficeKeysForm = ({ rowForEdit }) => {
  const [isOfficeKeysModal, setOfficeKeysModal] =
    useRecoilState(officeKeysAddState);
  const [isAddState] = useRecoilState(SecurityAddState);
  const [isSaving, setIsSaving] = useRecoilState(officeKeysSaving);
  const [isEditState] = useRecoilState(SecurityEditState);
  const [showOfficeKeyCardModal, setShowOfficeKeyCardModal] =
    useRecoilState(officeKeyModalState);
  const [showDeactivateModal, setShowDeactivateModal] =
    useRecoilState(okDeactivateModal);
  const [forDeleteDetails, setForDeleteDetails] = useState();
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [hasFirstFieldValue, setHasFirstFieldValue] = useState(true);
  const [clickCount, setClickCount] = useState(0);
  const [officeKeysForEdit, setOfficeKeysForEditData] =
    useRecoilState<any>(OfficeKeysForEdit);
  const [isAddInEditState, setIsAddInEditState] = useState(false);

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const deletePermission = PERMISSIONS.permission_security_delete;

  const defaultOfficeKsys = [
    {
      parentRow: true,
      tableName: "officeKey",
      lockSmithVendorId: null,
    },
  ] as Array<IOfficeKeys>;

  const officeKeys = defaultOfficeKsys;

  const formMethods = useFormContext();
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = formMethods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "officeKey",
  });

  const tableArray =
    officeKeys.length > 0 && watch(`${officeKeys[0].tableName}`);

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  useEffect(() => {
    if (officeKeys) {
      replace(officeKeys);
    }
  }, []);

  useEffect(() => {
    if (userPermissions.includes(deletePermission)) {
      setHasDeletePermission(true);
    }
  }, []);

  const { data: Vendors } = useVendors();
  const vendorsOption = Vendors?.data?.map(
    (x: {
      id: number;
      name: string;
      contact: string;
      email: string;
      city: string;
      accountNumber: string;
      state: string;
      street: string;
      vendorType: number;
      zipCode: string;
      phone: string;
      tonerRequestEmail: string;
      tonerRequestPhone: string;
    }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.name,
        email: x.email,
        accountNumber: x.accountNumber,
        address:
          (x.street ? x.street : "") +
          " " +
          (x.city ? x.city : "") +
          ", " +
          (x.state ? x.state : "") +
          " " +
          (x.zipCode ? x.zipCode : ""),
        vendorType: x.vendorType,
        vendorPhone: x.phone,
        trEmail: x.tonerRequestEmail,
        tonerPhone: x.tonerRequestPhone,
        contact: x.contact,
      };
    }
  );

  const { data: employees } = useEmployees();
  const employeeList = employees
    ?.filter((b) => b.status === "Active")
    .map(
      (x: {
        employeenumber: number;
        employeeId: number;
        fullname: string;
        email: string;
        status: string;
        reportsToFullname: string;
        officeName: string;
        firstname: string;
        middlename: string;
        lastname: string;
      }) => {
        return {
          id: x.employeeId,
          value: x.employeeId?.toString(),
          label:
            x.firstname +
            (x.middlename ? " " + x.middlename + " " : " ") +
            x.lastname,
          email: x.email,
          status: x.status,
          manager: x.reportsToFullname,
          officeName: x.officeName,
          fullName:
            x.firstname +
            (x.middlename ? " " + x.middlename + " " : " ") +
            x.lastname,
        };
      }
    );

  const { data: numberOfKeys } = useNumberOfKeys();
  const numberOfKeysOption = numberOfKeys?.data?.map(
    (x: { id: number; numberOfKeys: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.numberOfKeys,
      };
    }
  );

  // For Key Type Field
  const { data: keyTypeData } = useGetKeyType();
  const keyTypeOpt = keyTypeData?.data?.map(
    (x: { keyTypeId: number; keyType: string }) => {
      return {
        id: x.keyTypeId,
        value: x.keyTypeId,
        label: x.keyType,
      };
    }
  );

  const queryClient = useQueryClient();

  //Add Validation
  const checkEmptyFields = (buttonState) => {
    if (buttonState === "add") {
      setClickCount(clickCount + 1);
      watch("officeKey[0].lockSmithVendorId")
        ? setHasFirstFieldValue(true)
        : setHasFirstFieldValue(false);
    }
    if (buttonState === "proceed") {
      const fieldLen = controlledFields.length;
      let hasValueCount = 0;

      controlledFields.forEach((obj: IOfficeKeys, i: number) =>
        Object.entries(obj)?.forEach(([key, value]) => {
          if (key === "lockSmithVendorId") {
            if (value) {
              hasValueCount = hasValueCount + 1;
              if (fieldLen === hasValueCount) {
                setOfficeKeysModal(false);
              }
            } else {
              setClickCount(clickCount + 1);
              toast.fire(
                "Information",
                "Please fill the LockSmith Vendor to proceed.",
                "info"
              );
              setHasFirstFieldValue(false);
            }
          }
        })
      );
    }
    if (buttonState === "saveEdit") {
      setClickCount(clickCount + 1);
      watch("lockSmithVendorId")
        ? handleEditSave()
        : toast.fire(
            "Information",
            "Please fill the LockSmith Vendor to save.",
            "info"
          );
    }
  };

  useEffect(() => {
    !hasFirstFieldValue &&
      toast.fire(
        "Information",
        "Please fill the LockSmith Vendor to add new.",
        "info"
      );
  }, [clickCount]);

  //Single Add Initialization
  const {
    mutate: addSingleOfficeKey,
    isSuccess: addOfficeKeySuccess,
    data: addOfficeKeyResult,
    isError: addAlarmError,
    reset: resetAddOfficeKeyData,
  } = useAddSingleOfficeKeys();

  //Edit Single Initialization
  const {
    mutate: editSingleOfficeKey,
    isSuccess: editOfficeKeySuccess,
    data: editOfficeKeyResult,
    isError: editAlarmError,
    reset: resetEditOfficeKeyData,
  } = useEditSingleOfficeKey();

  //Delete
  const {
    mutate: deleteOfficeKey,
    isSuccess: deleteOfficeKeySuccess,
    data: deleteOfficeKeyResult,
    isError: deleteOfficeKeyError,
    reset: resetDeleteOfficeKeyData,
  } = useDeleteOfficeKey();
  //Reset form data sing Add
  const resetData = () => {
    formMethods.setValue("officeKey[0].lockSmithVendorId", "");
    formMethods.setValue("officeKey[0].okVendorPhone", "");
    formMethods.setValue("officeKey[0].okVendorAddress", "");
    formMethods.setValue("officeKey[0].keysAssignedTo", "");
    formMethods.setValue("officeKey[0].numberOfKeys", "");
    formMethods.setValue("officeKey[0].keyCodingModel", "");
    formMethods.setValue("officeKey[0].keyTypeId", "");
  };
  //Toasters
  // Add Success
  if (addOfficeKeySuccess && addOfficeKeyResult) {
    if (
      addOfficeKeyResult.status === 201 ||
      addOfficeKeyResult.status === 200
    ) {
      toast.fire("Success", "Office Key added successfully.", "success");
      queryClient.invalidateQueries("GetOfficeKeysByRecordNumber");
    }
    setIsSaving(false);
    resetAddOfficeKeyData();
    resetData();
  }

  // Edit Success
  if (editOfficeKeySuccess && editOfficeKeyResult) {
    if (
      editOfficeKeyResult.status === 201 ||
      editOfficeKeyResult.status === 200
    ) {
      toast.fire("Success", "Office Key updated successfully.", "success");
      queryClient.invalidateQueries("GetOfficeKeysByRecordNumber");
      setShowOfficeKeyCardModal(false);
    }
    setIsSaving(false);
    resetEditOfficeKeyData();
  }

  // Delete Success
  if (deleteOfficeKeySuccess && deleteOfficeKeyResult) {
    if (
      deleteOfficeKeyResult.status === 201 ||
      deleteOfficeKeyResult.status === 200
    ) {
      toast.fire("Success", "Office key deactivated successfully.", "success");
      queryClient.invalidateQueries("GetOfficeKeysByRecordNumber");
    }
    resetDeleteOfficeKeyData();
  }

  //function for Add
  const handleSave = () => {
    setIsSaving(true);
    const officeKeyData = {
      recordNumber: rowForEdit?.recordNumber,
      lockSmithVendorId: formMethods.getValues(
        "officeKey[0].lockSmithVendorId"
      ),
      okVendorAddress: formMethods.getValues("officeKey[0].okVendorAdress"),
      okVendorPhone: formMethods.getValues("officeKey[0].okVendorPhone"),
      numberOfKeys: formMethods.getValues("officeKey[0].numberOfKeys"),
      keysAssignedTo: formMethods.getValues("officeKey[0].keysAssignedTo"),
      keyTypeId: formMethods.getValues("officeKey[0].keyTypeId"),
      keyCodingModel: formMethods.getValues("officeKey[0].keyCodingModel"),
    };
    addSingleOfficeKey(officeKeyData);
    setOfficeKeysModal(false);
  };

  const { data: officeKeysData } = useGetOfficeKeysByRecordNumber(
    rowForEdit ? rowForEdit?.recordNumber : 0
  );
  const setEditOfficeKeyValues = (officeKey) => {
    const keysValue = officeKey?.keysAssignedTo?.split(",");
    const filteredValue = keysValue?.map((x) =>
      employeeList?.filter((b) => b.value === x)
    );

    const keysAssignedValue = filteredValue?.map((x) => x?.map((x) => x)[0]);
    formMethods.setValue("id", officeKey.id);
    formMethods.setValue("lockSmithVendorId", officeKey.lockSmithVendorId);
    formMethods.setValue("okvendorPhone", officeKey.okvendorPhone);
    formMethods.setValue(
      "okvendorAddress",
      officeKey.street +
        " " +
        officeKey.city +
        ", " +
        officeKey.state +
        " " +
        officeKey.zipCode
    );
    formMethods.setValue(
      "keysAssignedTo",
      officeKey.keysAssignedTo ? keysAssignedValue : null
    );
    formMethods.setValue("keyTypeId", officeKey.keyTypeId);
    formMethods.setValue("numberOfKeys", officeKey.numberOfKeys);
    formMethods.setValue("keyCodingModel", officeKey.keyCodingModel);
    formMethods.setValue("isActive", officeKey.isActive);
    formMethods.setValue("createdBy", officeKey.createdBy);
    formMethods.setValue("createdDate", officeKey.createdDate);

    queryClient.invalidateQueries("GetOfficeKeysByRecordNumber");
    setShowOfficeKeyCardModal(true);
  };

  const handleEditSave = () => {
    const officeKeyVal = formMethods.getValues("keysAssignedTo");
    const officeKeyData =
      officeKeyVal && officeKeyVal.map((x) => x.value)?.join(",");

    const newData = {
      recordNumber: rowForEdit?.recordNumber,
      id: formMethods.getValues("id"),
      lockSmithVendorId: formMethods.getValues("lockSmithVendorId"),
      okVendorAddress: formMethods.getValues("okVendorAdress"),
      okVendorPhone: formMethods.getValues("okVendorPhone"),
      numberOfKeys: formMethods.getValues("numberOfKeys"),
      keysAssignedTo: officeKeyData,
      keyTypeId: formMethods.getValues("keyTypeId"),
      keyCodingModel: formMethods.getValues("keyCodingModel"),
      isActive: formMethods.getValues("isActive"),
      createdBy: formMethods.getValues("createdBy"),
      createdDate: formMethods.getValues("createdDate"),
    };
    const oldData = {
      recordNumber: officeKeysForEdit?.recordNumber,
      id: officeKeysForEdit?.id,
      lockSmithVendorId: officeKeysForEdit?.lockSmithVendorId,
      numberOfKeys: officeKeysForEdit?.numberOfKeys,
      keysAssignedTo: officeKeysForEdit?.keysAssignedTo,
      keyTypeId: officeKeysForEdit?.keyTypeId,
      keyCodingModel: officeKeysForEdit?.keyCodingModel,
      isActive: officeKeysForEdit?.isActive,
      createdBy: officeKeysForEdit?.createdBy,
      createdDate: officeKeysForEdit?.createdDate,
    };
    const okNewData = JSON.stringify(newData);
    const okOldData = JSON.stringify(oldData);
    if (okNewData === okOldData) {
      toast.fire("Saving Failed", "No changes has been made.", "warning");
      setIsSaving(false);
    } else {
      setIsSaving(true);
      editSingleOfficeKey(newData);
    }
  };

  const handleCancel = () => {
    setOfficeKeysModal(false);
    setShowOfficeKeyCardModal(false);
    formMethods.clearErrors();
    formMethods.reset();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate this Office Key?"}
    </div>
  );

  //Deactivate
  const handleDeactivate = () => {
    deleteOfficeKey(forDeleteDetails);
    setShowDeactivateModal(false);
  };

  return (
    <div className="mx-2 mt-2 bg-white box-shadow rounded-lg modal-content">
      <div className="flex flex-row justify-between py-2">
        <div className="text-quinary font-bold font-sans px-4">
          Office Keys Section
        </div>
        {userPermissions.includes(addPermission) ? (
          <div className="py-2 px-4">
            <AddButton
              className={"opacity-100"}
              disabled={
                officeKeysData !== undefined
                  ? officeKeysData.length >= 5
                    ? true
                    : false
                  : false
              }
              buttonText="Add Office Keys"
              onClick={() => {
                setOfficeKeysModal(true);
                setIsAddInEditState(true);
              }}
            />
          </div>
        ) : (
          <div className="py-5 px-4"></div>
        )}
      </div>
      {isAddState && (
        <div className="text-quinary font-bold font-sans py-2 px-4">
          <h1
            className="mr-auto truncate w-full cursor-pointer text-ellipsis font-bold text-primary underline text-lg uppercase"
            onClick={() => {
              setOfficeKeysModal(true);
            }}
          >
            {controlledFields &&
              controlledFields[0]?.lockSmithVendorId &&
              "View Office Keys/s"}
          </h1>
        </div>
      )}
      {/* Show card edit button if edit state*/}
      {isEditState && (
        <div className="w-full borderContainer -mt-4 pb-4 bg-white px-4">
          <div className="grid gap-2 grid-cols-5">
            {/* Card */}
            {officeKeysData?.map((keys) => (
              <div key={keys.id}>
                <OfficeKeyCard
                  officeKey={keys}
                  isEditState={isEditState}
                  hasDeletePermission={hasDeletePermission}
                  onClickEdit={() => {
                    setEditOfficeKeyValues(keys);
                    setOfficeKeysForEditData(keys);
                  }}
                  onClickView={() => {}}
                  onClickDelete={() => {
                    setShowDeactivateModal(true);
                    setForDeleteDetails(keys);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Add Modal */}
      {isOfficeKeysModal && (
        <SecurityModal
          isCloseHide={true}
          modalFilterStat=""
          className=""
          width={""}
          title={""}
          isOpen={isOfficeKeysModal}
          onClose={undefined}
          hasCloseButton={false}
        >
          <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState || isAddInEditState
                ? "Add Office Keys"
                : "Edit Office Keys"}
            </div>
            {isAddState ? (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={"Proceed"}
                  className={undefined}
                  onClick={() => {
                    checkEmptyFields("proceed");
                    setHasFirstFieldValue(true);
                  }}
                />
                {/* <CancelButton
                  onClick={() => {
                    setOfficeKeysModal(false);
                    resetData();
                    remove([1, 2, 3, 4]);
                  }}
                /> */}
                <div className="relative -top-2 -right-6">
                  <CloseIconButton onClick={() => setOfficeKeysModal(false)} />
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="button"
                  disabled={false}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={() => {
                    watch("officeKey[0].lockSmithVendorId")
                      ? handleSave()
                      : toast.fire(
                          "Information",
                          "Please fill the vendor field to save.",
                          "info"
                        );
                  }}
                />
                <CancelButton
                  onClick={() => {
                    setOfficeKeysModal(false);
                    resetData();
                  }}
                />
              </div>
            )}
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="text-xs text-secondary italic font-normal my-2 ml-2">
            <b> Note:</b> All fields with * are required to fill.
          </div>
          <div
            className="box-shadow rounded-md"
            style={
              controlledFields.length > 2
                ? {
                    height: "70vh",
                    overflow: "auto",
                  }
                : undefined
            }
          >
            {controlledFields &&
              controlledFields.map((field, index) => {
                return (
                  <>
                    <div className="ml-4 py-2" key={field.id}>
                      {index > 0 ? (
                        <MinusButton
                          type="button"
                          onClick={() => remove(index)}
                        />
                      ) : (
                        !isEditState && (
                          <PlusButton
                            type="button"
                            onClick={(e) => {
                              if (controlledFields.length < 5) {
                                checkEmptyFields("add");
                                watch(`officeKey[0].lockSmithVendorId`) &&
                                  append({
                                    tableName: officeKeys[0]?.tableName,
                                    parentRow: false,
                                  });
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
                      <div className="space-y-1">
                        <Controller
                          name={`${officeKeys[0].tableName}[${index}].lockSmithVendorId`}
                          control={formMethods.control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                                formMethods.setValue(
                                  `${officeKeys[0].tableName}[${index}].okVendorAddress`,
                                  e ? e.address : null
                                );
                                formMethods.setValue(
                                  `${officeKeys[0].tableName}[${index}].okVendorPhone`,
                                  e ? e.vendorPhone : null
                                );
                              }}
                              value={field.value}
                              options={vendorsOption}
                              label="LockSmith Vendor"
                              className={
                                formMethods.formState.errors
                                  .lockSmithVendorId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={true}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${officeKeys[0].tableName}[${index}].okVendorAddress`}
                          label="Vendor Address"
                          type="text"
                          register={formMethods.register(
                            `${officeKeys[0].tableName}[${index}].okVendorAddress`,
                            {}
                          )}
                          maxLength={50}
                          disabled={true}
                        />
                        <TextInputLabelHookForm
                          name={`${officeKeys[0].tableName}[${index}].okVendorPhone`}
                          label="Vendor Phone"
                          type="text"
                          register={formMethods.register(
                            `${officeKeys[0].tableName}[${index}].okVendorPhone`,
                            {}
                          )}
                          maxLength={50}
                          disabled={true}
                        />
                        <Controller
                          name={`${officeKeys[0].tableName}[${index}].numberOfKeys`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={numberOfKeysOption}
                              label="# of Keys"
                              className={
                                formMethods.formState.errors.numberOfKeys &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                      </div>
                      <div className="space-y-1 mr-6">
                        <Controller
                          name={`${officeKeys[0].tableName}[${index}].keysAssignedTo`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookForm_3
                              label="Keys Assigned To"
                              field={field}
                              onChange={(e) => {
                                const keysTo = e?.map((x) => x.value);
                                field.onChange(e);
                              }}
                              value={
                                field.value
                                  ? field.value.filter(
                                      (item) => item !== undefined
                                    )
                                  : undefined
                              }
                              options={employeeList}
                              isMulti={true}
                              className={""}
                              validation={undefined}
                              defaultValue={undefined}
                              isDisabled={undefined}
                              isLoading={undefined}
                            />
                          )}
                        />
                        <Controller
                          name={`${officeKeys[0].tableName}[${index}].keyTypeId`}
                          control={formMethods.control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                              }}
                              value={field.value}
                              options={keyTypeOpt}
                              label="Key Type"
                              className={
                                formMethods.formState.errors.keyTypeId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={false}
                            />
                          )}
                        />
                        <TextInputLabelHookForm
                          name={`${officeKeys[0].tableName}[${index}].keyCodingModel`}
                          label="Key Coding Model"
                          type="text"
                          register={formMethods.register(
                            `${officeKeys[0].tableName}[${index}].keyCodingModel`,
                            {}
                          )}
                          maxLength={100}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="w-full border-b-2 border-grey-200"></div>
                    </div>
                  </>
                );
              })}
          </div>
        </SecurityModal>
      )}
      {/* Edit Modal */}
      {showOfficeKeyCardModal && (
        <Modal
          isCloseHide={true}
          isOpen={showOfficeKeyCardModal}
          onClose={undefined}
          title={undefined}
          className={"w-auto"}
          modalFilterStat={undefined}
          hasCloseButton={false}
          width={"auto"}
        >
          <div className="flex flex-row justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
            <div className="ml-2 font-bold text-primary text-md">
              {isAddState ? "Add Office Keys" : "Edit Office Keys"}
            </div>
            <div className="flex justify-between gap-2 mr-2">
              <SubmitHookForm
                type="button"
                disabled={false}
                buttonText={isSaving ? "Saving.." : "Save"}
                className={undefined}
                onClick={() => {
                  checkEmptyFields("saveEdit");
                }}
              />
              <CancelButton
                onClick={() => {
                  setOfficeKeysModal(false);
                  setShowOfficeKeyCardModal(false);
                }}
              />
            </div>
          </div>
          <div className="w-full border-b-2 border-secondary mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 m-4 gap-4">
            <div className="space-y-1 mr-6">
              <Controller
                name="lockSmithVendorId"
                control={formMethods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                      formMethods.setValue(
                        "okvendorAddress",
                        e ? e.address : null
                      );
                      formMethods.setValue(
                        "okvendorPhone",
                        e ? e.vendorPhone : null
                      );
                    }}
                    value={field.value}
                    options={vendorsOption}
                    label="LockSmith Vendor"
                    className={
                      formMethods.formState.errors.lockSmithVendorId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={true}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="okvendorAddress"
                label="Vendor Address"
                type="text"
                register={formMethods.register("okvendorAddress", {})}
                maxLength={50}
                disabled={true}
              />
              <TextInputLabelHookForm
                name="okvendorPhone"
                label="Vendor Phone"
                type="text"
                register={formMethods.register("okvendorPhone", {})}
                maxLength={50}
                disabled={true}
              />
              <Controller
                name="numberOfKeys"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={numberOfKeysOption}
                    label="# of Keys"
                    className={
                      formMethods.formState.errors.numberOfKeys &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
            </div>
            <div className="space-y-1 mr-6">
              <Controller
                name="keysAssignedTo"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookForm_3
                    label="Keys Assigned To"
                    field={field}
                    onChange={(e) => {
                      const keysTo = e?.map((x) => x.value);
                      field.onChange(e);
                    }}
                    value={
                      field.value
                        ? field.value.filter((item) => item !== undefined)
                        : undefined
                    }
                    options={employeeList}
                    isMulti={true}
                    className={""}
                    validation={undefined}
                    defaultValue={undefined}
                    isDisabled={undefined}
                    isLoading={undefined}
                  />
                )}
              />
              <Controller
                name="keyTypeId"
                control={formMethods.control}
                rules={{ required: false }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    field={field}
                    onChange={(e) => {
                      field.onChange(e ? e.value : null);
                    }}
                    value={field.value}
                    options={keyTypeOpt}
                    label="Key Type"
                    className={
                      formMethods.formState.errors.keyTypeId &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    required={false}
                  />
                )}
              />
              <TextInputLabelHookForm
                name="keyCodingModel"
                label="Key Coding Model"
                type="text"
                register={formMethods.register("keyCodingModel", {})}
                maxLength={100}
              />
              {isEditState && hasDeletePermission && (
                <CheckBoxLabelHookForm
                  name="isActive"
                  type="checkbox"
                  register={register}
                  label="Active Account"
                  className="cursor-pointer auto-rows-auto text-right justify-items-center"
                />
              )}
            </div>
          </div>
        </Modal>
      )}
      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmBoxDelete
          ModalTitle="Security Office Key"
          qConfirmation={deleteMessage}
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onClickFunction={() => handleDeactivate()}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};

export default OfficeKeysForm;
